/*
 * AYA — Install Prompt UI
 * ---------------------------------------------------------------------------
 * Banner "Installer AYA pour un accès instantané" affiché quand :
 *   1. L'utilisateur a atteint un "value milestone" (voir valueSignals.js)
 *   2. AYA n'est PAS déjà installée (display-mode standalone)
 *   3. L'utilisateur n'a pas dismissé le prompt récemment (7 jours)
 *   4. beforeinstallprompt event dispo (Chrome/Android) OU iOS Safari (instructions)
 *
 * Update toast : discret, en bas centré, "Nouvelle version — Recharger".
 * ---------------------------------------------------------------------------
 */

import React, { useEffect, useState } from "react";
import {
  isInstallPromptAvailable, triggerInstallPrompt,
  subscribeInstallEvents, subscribeUpdateEvents, acceptUpdate,
  isIOS, isStandalone,
} from "./registerSW";
import { hasMilestone, wasDismissed, markDismissed, subscribeSignals } from "./valueSignals";

export default function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [installMode, setInstallMode] = useState(null); // 'native' | 'ios' | null
  const [updateReg, setUpdateReg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // déjà installée → jamais afficher

    const evaluate = () => {
      if (isStandalone()) { setVisible(false); return; }
      if (!hasMilestone()) { setVisible(false); return; }
      if (wasDismissed()) { setVisible(false); return; }
      if (isInstallPromptAvailable()) { setInstallMode('native'); setVisible(true); return; }
      if (isIOS()) { setInstallMode('ios'); setVisible(true); return; }
      setVisible(false);
    };

    evaluate();
    const unsubSignals = subscribeSignals(() => evaluate());
    const unsubInstall = subscribeInstallEvents((state) => {
      if (state === 'installed') setVisible(false);
      else evaluate();
    });
    // Update toast (indépendant)
    const unsubUpdate = subscribeUpdateEvents((reg) => setUpdateReg(reg));
    return () => { unsubSignals(); unsubInstall(); unsubUpdate(); };
  }, []);

  const handleInstall = async () => {
    if (installMode !== 'native') return;
    setBusy(true);
    const res = await triggerInstallPrompt();
    setBusy(false);
    if (res.outcome === 'accepted') setVisible(false);
  };

  const handleDismiss = () => {
    markDismissed();
    setVisible(false);
  };

  return (
    <>
      {visible && installMode === 'native' && (
        <div data-testid="pwa-install-banner" role="dialog" aria-label="Installer AYA" style={styles.banner}>
          <div style={styles.icon} aria-hidden>
            <img src="/pwa/icon-192.png" alt="" style={{ width: 44, height: 44, borderRadius: 10 }} />
          </div>
          <div style={styles.body}>
            <div style={styles.title}>Installer AYA</div>
            <div style={styles.subtitle}>Un accès instantané, sans passer par le navigateur — comme une vraie app.</div>
          </div>
          <div style={styles.actions}>
            <button data-testid="pwa-install-dismiss" onClick={handleDismiss} style={styles.btnGhost}>Plus tard</button>
            <button data-testid="pwa-install-accept" onClick={handleInstall} disabled={busy} style={styles.btnPrimary}>{busy ? "…" : "Installer"}</button>
          </div>
        </div>
      )}

      {visible && installMode === 'ios' && (
        <IOSInstallGuide onDismiss={handleDismiss} />
      )}

      {updateReg && (
        <div data-testid="pwa-update-toast" role="status" style={styles.updateToast}>
          <span style={{ fontSize: 12.5 }}>✨ Nouvelle version d&apos;AYA prête</span>
          <button
            data-testid="pwa-update-reload"
            onClick={() => acceptUpdate(updateReg)}
            style={styles.updateBtn}
          >Recharger</button>
        </div>
      )}
    </>
  );
}

function IOSInstallGuide({ onDismiss }) {
  return (
    <div data-testid="pwa-install-banner-ios" role="dialog" aria-label="Installer AYA sur iOS" style={styles.banner}>
      <div style={styles.icon}>
        <img src="/pwa/icon-192.png" alt="" style={{ width: 44, height: 44, borderRadius: 10 }} />
      </div>
      <div style={styles.body}>
        <div style={styles.title}>Ajouter AYA à l&apos;écran d&apos;accueil</div>
        <div style={styles.subtitle}>
          Appuie sur <IosShareIcon /> puis <strong>« Sur l&apos;écran d&apos;accueil »</strong>.
        </div>
      </div>
      <div style={styles.actions}>
        <button data-testid="pwa-install-ios-dismiss" onClick={onDismiss} style={styles.btnGhost}>×</button>
      </div>
    </div>
  );
}

function IosShareIcon() {
  return (
    <svg width="14" height="16" viewBox="0 0 20 24" fill="none" style={{ verticalAlign: "middle" }}>
      <path d="M10 3v13m0-13l-4 4m4-4l4 4M4 15v5a2 2 0 002 2h8a2 2 0 002-2v-5" stroke="#EBD9A8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const styles = {
  banner: {
    position: "fixed",
    left: 12, right: 12,
    bottom: "calc(env(safe-area-inset-bottom, 0px) + 88px)",
    zIndex: 90,
    display: "flex", alignItems: "center", gap: 12,
    padding: "12px 14px",
    borderRadius: 16,
    background: "linear-gradient(135deg, #073527, #032117)",
    border: "1px solid rgba(232,213,163,0.35)",
    boxShadow: "0 12px 40px rgba(0,0,0,0.55)",
    color: "#F4F1E8",
    maxWidth: 560, marginLeft: "auto", marginRight: "auto",
    animation: "ayaPwaIn 380ms cubic-bezier(0.16, 1, 0.3, 1)",
  },
  icon: { flexShrink: 0 },
  body: { flex: 1, minWidth: 0 },
  title: { fontSize: 14, fontWeight: 800, marginBottom: 2 },
  subtitle: { fontSize: 11.5, opacity: 0.75, lineHeight: 1.45 },
  actions: { display: "flex", gap: 6, flexShrink: 0 },
  btnGhost: {
    padding: "8px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700,
    background: "transparent", color: "rgba(244,241,232,0.75)",
    border: "1px solid rgba(244,241,232,0.2)", cursor: "pointer",
  },
  btnPrimary: {
    padding: "8px 16px", borderRadius: 999, fontSize: 12.5, fontWeight: 800,
    background: "linear-gradient(135deg, #F5E9C6, #E4C87F 55%, #CDA452)",
    color: "#3A2E10", border: "none", cursor: "pointer",
    boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
  },
  updateToast: {
    position: "fixed",
    left: "50%",
    top: "calc(env(safe-area-inset-top, 0px) + 12px)",
    transform: "translateX(-50%)",
    zIndex: 91,
    display: "flex", alignItems: "center", gap: 10,
    padding: "8px 14px",
    borderRadius: 999,
    background: "rgba(11,53,39,0.95)",
    border: "1px solid rgba(232,213,163,0.35)",
    color: "#F4F1E8",
    backdropFilter: "blur(8px)",
    boxShadow: "0 6px 24px rgba(0,0,0,0.4)",
    animation: "ayaPwaIn 300ms ease-out",
  },
  updateBtn: {
    padding: "5px 12px", borderRadius: 999, fontSize: 11.5, fontWeight: 800,
    background: "#EBD9A8", color: "#3A2E10", border: "none", cursor: "pointer",
  },
};

// Style tag injecté une seule fois
if (typeof document !== 'undefined' && !document.getElementById('aya-pwa-anim')) {
  const s = document.createElement('style');
  s.id = 'aya-pwa-anim';
  s.textContent = '@keyframes ayaPwaIn { from { transform: translateY(24px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }';
  document.head.appendChild(s);
}
